import { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import Icon from '../components/Icon';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import Modal from '../components/ui/Modal';
import Spinner from '../components/ui/Spinner';
import Toggle from '../components/ui/Toggle';
import ResponseConfigForm, { type ResponseConfig } from '../components/ResponseConfigForm';
import { Card, CardBody } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { useAccounts } from '../contexts/AccountContext';
import {
  listFlows,
  getFlow,
  createFlow,
  updateFlow,
  setFlowEnabled,
  deleteFlow,
  type FlowSummary,
} from '../lib/sections-api';

const EMPTY_CONFIG: ResponseConfig = { replyText: '', steps: [], buttons: [] };

export default function DmRulesPage() {
  useDocumentTitle('دایرکت');
  const { selectedAccountId } = useAccounts();
  const [flows, setFlows] = useState<FlowSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [keyword, setKeyword] = useState('');
  const [config, setConfig] = useState<ResponseConfig>(EMPTY_CONFIG);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (selectedAccountId === null) return;
    let cancelled = false;
    listFlows('dm', selectedAccountId)
      .then((list) => {
        if (!cancelled) setFlows(list);
      })
      .catch(() => {
        if (!cancelled) setFlows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedAccountId, reloadKey]);

  const reload = (): void => setReloadKey((k) => k + 1);

  const openNew = (): void => {
    setEditingId(null);
    setKeyword('');
    setConfig(EMPTY_CONFIG);
    setModalOpen(true);
  };

  const openEdit = async (flow: FlowSummary): Promise<void> => {
    setEditingId(flow.id);
    setKeyword(flow.keywords[0] ?? '');
    setError(null);
    try {
      const detail = await getFlow('dm', flow.id);
      setConfig({
        replyText: detail.flow.reply_text ?? '',
        steps: detail.steps.map((s) => ({
          kind: s.kind,
          text: s.text,
          media_asset_id: s.media_asset_id,
        })),
        buttons: detail.buttons
          .filter((b) => b.parent_button_id === null)
          .map((b) => ({
            label: b.label,
            action_kind: b.action_kind,
            text: b.text,
            media_asset_id: b.media_asset_id,
            url: b.url,
            children: detail.buttons
              .filter((c) => c.parent_button_id === b.id)
              .map((c) => ({
                label: c.label,
                action_kind: c.action_kind,
                text: c.text,
                media_asset_id: c.media_asset_id,
                url: c.url,
              })),
          })),
      });
    } catch {
      setConfig(EMPTY_CONFIG);
    }
    setModalOpen(true);
  };

  const handleSave = async (): Promise<void> => {
    if (selectedAccountId === null || keyword.trim() === '') return;
    setSaving(true);
    setError(null);
    const payload = {
      instagram_account_id: selectedAccountId,
      keywords: [keyword.trim()],
      reply_text: config.replyText.trim() === '' ? null : config.replyText,
      steps: config.steps,
      buttons: config.buttons,
    };
    try {
      if (editingId !== null) {
        await updateFlow('dm', editingId, payload);
      } else {
        await createFlow('dm', payload);
      }
      setModalOpen(false);
      reload();
    } catch {
      setError('ذخیره قانون ممکن نشد.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (flow: FlowSummary, enabled: boolean): Promise<void> => {
    try {
      await setFlowEnabled('dm', flow.id, enabled);
      reload();
    } catch {
      // ignore
    }
  };

  const handleDelete = async (flow: FlowSummary): Promise<void> => {
    try {
      await deleteFlow('dm', flow.id);
      reload();
    } catch {
      // ignore
    }
  };

  return (
    <div>
      <PageHeader
        title="دایرکت"
        description="پاسخ خودکار به پیام‌های مستقیم بر اساس کلیدواژه"
        actions={
          <Button onClick={openNew} size="sm">
            <Icon name="plus" className="h-4 w-4" />
            قانون جدید
          </Button>
        }
      />

      {error !== null && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700">
          <Icon name="alert" className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {selectedAccountId === null ? (
        <EmptyState
          icon="dm"
          title="حسابی انتخاب نشده"
          description="از بالای صفحه یک حساب انتخاب کنید."
        />
      ) : loading ? (
        <Spinner className="py-8" />
      ) : flows.length === 0 ? (
        <EmptyState
          icon="dm"
          title="قانونی وجود ندارد"
          description="اولین قانون پاسخ خودکار دایرکت را بسازید."
        />
      ) : (
        <div className="space-y-2">
          {flows.map((flow) => (
            <Card key={flow.id}>
              <CardBody className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge tone="info">کلیدواژه</Badge>
                    <span className="text-sm font-semibold text-slate-800">
                      {flow.keywords[0] ?? '—'}
                    </span>
                  </div>
                  <p className="mt-1 truncate text-xs text-slate-500">
                    {flow.reply_text ?? 'بدون متن پاسخ'}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Toggle checked={flow.enabled} onChange={(v) => void handleToggle(flow, v)} />
                  <Button size="sm" variant="ghost" onClick={() => void openEdit(flow)}>
                    ویرایش
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => void handleDelete(flow)}>
                    <Icon name="trash" className="h-4 w-4 text-rose-500" />
                  </Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId !== null ? 'ویرایش قانون' : 'قانون جدید'}
        wide
      >
        <div className="mb-4">
          <Input
            label="کلیدواژه"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="متنی که کاربر می‌فرستد (مثلاً «قیمت»)"
          />
        </div>
        <ResponseConfigForm value={config} onChange={setConfig} />
        <div className="mt-5 flex justify-end gap-2 border-t border-slate-100 pt-4">
          <Button variant="secondary" onClick={() => setModalOpen(false)}>
            انصراف
          </Button>
          <Button
            onClick={() => void handleSave()}
            loading={saving}
            disabled={keyword.trim() === ''}
          >
            ذخیره
          </Button>
        </div>
      </Modal>
    </div>
  );
}
